import { isSupabaseConfigured, missingSupabaseConfig } from "./config";
import { supabase } from "./client";
import { registerRestaurantWithContract } from "./restaurantContractService";
import { normalizeQrMenuStyle } from "../local/qrMenuStyle";
import { normalizeQrCategoryOrder, normalizeQrSubcategoryOrder } from "../local/qrCategoryOrder";
import {
  DEFAULT_WAITER_PERMISSIONS,
  mapWaiterPermissionsFromDb,
  mapWaiterPermissionsToBranchPermissions,
} from "../local/waiterPermissions";
import {
  DEFAULT_BRANCH_COMMISSION_RATE,
  DEFAULT_BRANCH_PAYMENT_PROVIDER,
  DEFAULT_BRANCH_PAYMENT_PROVIDER_STATUS,
  DEFAULT_BRANCH_PAYMENT_SPLIT_MODE,
  normalizeCommissionRate,
  normalizePaymentMethods,
  normalizePaymentProvider,
  normalizePaymentProviderStatus,
  normalizePaymentSplitMode,
} from "../local/paymentSettings";

const FULL_BRANCH_PERMISSIONS = {
  canViewMenu: true,
  canManageMenu: true,
  canViewStock: true,
  canManageStock: true,
  canManageOrders: true,
  canManageQr: true,
  canManagePayments: true,
  canManageSettings: true,
  ...mapWaiterPermissionsToBranchPermissions(
    {
      takeOrders: true,
      modifyOrders: true,
      cancelOrders: true,
      chargeOrders: true,
      refundOrders: true,
    },
    DEFAULT_WAITER_PERMISSIONS
  ),
};

const ensureSupabaseReady = () => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error(
      `Faltan variables de entorno para Supabase: ${missingSupabaseConfig.join(", ")}`
    );
  }
};

const normalizeError = (error, fallbackMessage) => {
  if (!error) {
    return new Error(fallbackMessage);
  }

  return new Error(error.message || fallbackMessage);
};

const normalizeNullableProfileText = (value) => {
  if (value === undefined) {
    return undefined;
  }

  if (value === null) {
    return "";
  }

  return `${value}`.trim();
};

const looksLikeMercadoPagoAppReference = (value) =>
  /^APP_USR-/i.test(
    value === undefined || value === null ? "" : `${value}`.trim()
  );

const isMissingWorkspaceRoleError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42703" ||
    message.includes("qr_menu_style") ||
    message.includes("workspace_role") ||
    (message.includes("column") && message.includes("does not exist"))
  );
};

const isMissingBranchSettingsColumnsError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42703" ||
    message.includes("qr_menu_style") ||
    message.includes("qr_category_order") ||
    message.includes("qr_subcategory_order") ||
    message.includes("service_mode") ||
    message.includes("kiosk_menu_mode") ||
    message.includes("kiosk_station_count") ||
    message.includes("kiosk_dine_in_image_url") ||
    message.includes("kiosk_takeaway_image_url") ||
    message.includes("qr_access_duration_minutes") ||
    message.includes("payment_provider") ||
    message.includes("payment_provider_status") ||
    message.includes("payment_account_reference") ||
    message.includes("payment_account_cbu") ||
    message.includes("payment_account_alias") ||
    message.includes("commission_rate") ||
    message.includes("payment_split_mode") ||
    message.includes("kiosk_payment_methods") ||
    message.includes("qr_payment_methods") ||
    message.includes("stock_enabled") ||
    message.includes("waiter_enabled") ||
    message.includes("ai_enabled") ||
    message.includes("salon_enabled") ||
    message.includes("delivery_enabled") ||
    message.includes("operational_setup_completed") ||
    message.includes("logo_compact_url") ||
    message.includes("icon_logo_url") ||
    message.includes("loading_logo_url") ||
    message.includes("loading_animation")
  );
};

const BRANCH_REMOTE_SETTINGS_SELECT =
  "branch_id, logo_url, logo_compact_url, icon_logo_url, loading_logo_url, cover_url, kiosk_dine_in_image_url, kiosk_takeaway_image_url, primary_color, secondary_color, font_family, loading_animation, welcome_message, kitchen_mode, service_mode, salon_enabled, delivery_enabled, operational_setup_completed, stock_enabled, waiter_enabled, ai_enabled, kiosk_menu_mode, kiosk_station_count, qr_access_duration_minutes, payment_provider, payment_provider_status, payment_account_reference, payment_account_cbu, payment_account_alias, commission_rate, payment_split_mode, kiosk_payment_methods, qr_payment_methods";

const BRANCH_REMOTE_SETTINGS_OPERATIONAL_SELECT =
  "branch_id, logo_url, cover_url, primary_color, secondary_color, font_family, welcome_message, kitchen_mode, service_mode, salon_enabled, delivery_enabled, operational_setup_completed, stock_enabled, waiter_enabled, ai_enabled, kiosk_menu_mode, kiosk_station_count";

const BRANCH_REMOTE_SETTINGS_KIOSK_SELECT =
  "branch_id, logo_url, cover_url, primary_color, secondary_color, font_family, welcome_message, kitchen_mode, service_mode, kiosk_menu_mode, kiosk_station_count";

const BRANCH_REMOTE_SETTINGS_LEGACY_SELECT =
  "branch_id, logo_url, cover_url, primary_color, secondary_color, font_family, welcome_message, kitchen_mode";

const ensureBranchSettingsSupportsKioskMode = async () => {
  const { error } = await supabase
    .from("branch_settings")
    .select("service_mode, kiosk_menu_mode, kiosk_station_count")
    .limit(1);

  if (error && isMissingBranchSettingsColumnsError(error)) {
    throw new Error(
      "Falta aplicar kiosk_mode_patch.sql en Supabase antes de guardar la modalidad fija de la sucursal."
    );
  }

  if (error) {
    throw normalizeError(
      error,
      "No se pudo validar la configuracion de modalidad de sucursales."
    );
  }
};

const createSlug = (name, fallback, suffix = "") => {
  const safeName = (name || fallback || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);

  const base = safeName || fallback;
  return suffix ? `${base}-${suffix}` : base;
};

const normalizeCompany = (company, role = "admin_general", workspaceRole = role) => ({
  id: company.id,
  name: company.name || "",
  slug: company.slug || "",
  legalName: company.legal_name || "",
  phone: company.phone || "",
  status: company.status || "active",
  role,
  workspaceRole: workspaceRole || role,
  createdAt: company.created_at || null,
});

const upsertCompanySettings = async (companyId, payload) => {
  const row = {
    company_id: companyId,
    billing_email: payload.billingEmail?.trim() || payload.ownerEmail?.trim() || null,
    support_phone: payload.supportPhone?.trim() || payload.companyPhone?.trim() || null,
    headquarters_address: payload.headquartersAddress?.trim() || null,
    legal_name: payload.legalName?.trim() || null,
    tax_id: payload.taxId?.trim() || null,
    owner_full_name: payload.ownerFullName?.trim() || null,
    owner_document_id: payload.ownerDocumentId?.trim() || null,
    owner_phone: payload.ownerPhone?.trim() || null,
    owner_email: payload.ownerEmail?.trim() || null,
    bank_account_holder:
      payload.bankAccountHolder?.trim() || payload.ownerFullName?.trim() || null,
    bank_name: payload.bankName?.trim() || null,
    bank_account_type: payload.bankAccountType?.trim() || null,
    bank_account_number: payload.bankAccountNumber?.trim() || null,
    bank_cbu: payload.bankCbu?.trim() || null,
    bank_alias: payload.bankAlias?.trim() || null,
  };

  const { error } = await supabase.from("company_settings").upsert(row);

  if (error) {
    throw normalizeError(error, "No se pudo guardar la informacion del titular y cobro.");
  }
};

const normalizeBranchPermissions = (accessRow) => {
  const waiterDefaults = {
    takeOrders: accessRow?.can_manage_orders === true,
    modifyOrders: accessRow?.can_manage_orders === true,
    cancelOrders: accessRow?.can_manage_orders === true,
    chargeOrders: accessRow?.can_manage_payments === true,
    refundOrders: accessRow?.can_manage_payments === true,
  };

  return {
    canViewMenu: accessRow?.can_view_menu === true || accessRow?.can_manage_menu === true,
    canManageMenu: accessRow?.can_manage_menu === true,
    canViewStock: accessRow?.can_view_stock === true || accessRow?.can_manage_stock === true,
    canManageStock: accessRow?.can_manage_stock === true,
    canManageOrders: accessRow?.can_manage_orders === true,
    canManageQr: accessRow?.can_manage_qr === true,
    canManagePayments: accessRow?.can_manage_payments === true,
    canManageSettings: accessRow?.can_manage_settings === true,
    ...mapWaiterPermissionsToBranchPermissions(
      mapWaiterPermissionsFromDb(accessRow, waiterDefaults),
      waiterDefaults
    ),
  };
};

const normalizeBranchMode = (value) => {
  if (value === "admin" || value === "mozo" || value === "cocina") {
    return value;
  }

  if (
    value === "admin_limitado" ||
    value === "admin_general" ||
    value === "encargado_sucursal" ||
    value === "caja" ||
    value === "soporte_mozzo"
  ) {
    return "admin";
  }

  return "";
};

const getDefaultBranchMode = (availableModes = []) => {
  if (availableModes.includes("admin")) {
    return "admin";
  }

  if (availableModes.includes("mozo")) {
    return "mozo";
  }

  if (availableModes.includes("cocina")) {
    return "cocina";
  }

  return "";
};

const resolveAvailableBranchModes = ({
  companyRole,
  companyWorkspaceRole,
  invitationModes,
}) => {
  const modes = new Set();

  if (companyRole === "admin_general" || companyWorkspaceRole === "admin_general") {
    modes.add("admin");
    modes.add("cocina");
  }

  (invitationModes || []).forEach((mode) => {
    const normalizedMode = normalizeBranchMode(mode);

    if (normalizedMode) {
      modes.add(normalizedMode);
    }
  });

  if (modes.size === 0) {
    const fallbackMode = normalizeBranchMode(companyWorkspaceRole);
    if (fallbackMode) {
      modes.add(fallbackMode);
    } else if (companyRole === "admin_limitado") {
      modes.add("admin");
    }
  }

  return Array.from(modes);
};

const normalizeBranchSettings = (settings) => ({
  qrMenuStyle: settings?.qr_menu_style === undefined ? undefined : normalizeQrMenuStyle(settings.qr_menu_style),
  qrCategoryOrder: settings?.qr_category_order === undefined ? undefined : normalizeQrCategoryOrder(settings.qr_category_order),
  qrSubcategoryOrder: settings?.qr_subcategory_order === undefined ? undefined : normalizeQrSubcategoryOrder(settings.qr_subcategory_order),
  serviceMode:
    typeof settings?.service_mode === "string"
      ? settings.service_mode === "kiosk"
        ? "kiosk"
        : "salon"
      : undefined,
  salonEnabled:
    settings?.salon_enabled === undefined || settings?.salon_enabled === null
      ? settings?.service_mode !== "kiosk"
      : settings.salon_enabled !== false,
  deliveryEnabled: settings?.delivery_enabled === true,
  operationalSetupCompleted: settings?.operational_setup_completed === true,
  stockEnabled: settings?.stock_enabled !== false,
  waiterEnabled: settings?.waiter_enabled !== false,
  aiEnabled: settings?.ai_enabled !== false,
  kioskMenuMode:
    typeof settings?.kiosk_menu_mode === "string"
      ? settings.kiosk_menu_mode === "custom"
        ? "custom"
        : "current"
      : undefined,
  kioskStationCount:
    settings?.kiosk_station_count === undefined || settings?.kiosk_station_count === null
      ? undefined
      : Math.min(12, Math.max(1, Number(settings.kiosk_station_count || 1) || 1)),
  qrAccessDurationMinutes:
    settings?.qr_access_duration_minutes === undefined ||
    settings?.qr_access_duration_minutes === null
      ? undefined
      : Math.min(
          720,
          Math.max(15, Number(settings.qr_access_duration_minutes || 120) || 120)
        ),
  paymentProvider:
    settings?.payment_provider === undefined || settings?.payment_provider === null
      ? DEFAULT_BRANCH_PAYMENT_PROVIDER
      : normalizePaymentProvider(settings.payment_provider),
  paymentProviderStatus:
    settings?.payment_provider_status === undefined ||
    settings?.payment_provider_status === null
      ? DEFAULT_BRANCH_PAYMENT_PROVIDER_STATUS
      : normalizePaymentProviderStatus(settings.payment_provider_status),
  paymentAccountReference: looksLikeMercadoPagoAppReference(
    settings?.payment_account_reference
  )
    ? ""
    : normalizeNullableProfileText(settings?.payment_account_reference),
  paymentAccountCbu: normalizeNullableProfileText(settings?.payment_account_cbu),
  paymentAccountAlias: normalizeNullableProfileText(
    settings?.payment_account_alias
  ),
  commissionRate:
    settings?.commission_rate === undefined || settings?.commission_rate === null
      ? DEFAULT_BRANCH_COMMISSION_RATE
      : normalizeCommissionRate(settings.commission_rate),
  paymentSplitMode:
    settings?.payment_split_mode === undefined || settings?.payment_split_mode === null
      ? DEFAULT_BRANCH_PAYMENT_SPLIT_MODE
      : normalizePaymentSplitMode(settings.payment_split_mode),
  kioskPaymentMethods: normalizePaymentMethods(settings?.kiosk_payment_methods, "kiosk"),
  qrPaymentMethods: normalizePaymentMethods(settings?.qr_payment_methods, "qr"),
  logoUrl: settings?.logo_url || settings?.logo_compact_url || undefined,
  logoCompactUrl: settings?.logo_compact_url || settings?.logo_url || undefined,
  iconLogoUrl:
    settings?.icon_logo_url ||
    settings?.logo_compact_url ||
    settings?.logo_url ||
    undefined,
  loadingLogoUrl:
    settings?.loading_logo_url ||
    settings?.icon_logo_url ||
    settings?.logo_compact_url ||
    settings?.logo_url ||
    undefined,
  coverUrl: settings?.cover_url || undefined,
  kioskDineInImageUrl: settings?.kiosk_dine_in_image_url || undefined,
  kioskTakeawayImageUrl: settings?.kiosk_takeaway_image_url || undefined,
  primaryColor: settings?.primary_color || undefined,
  secondaryColor: settings?.secondary_color || undefined,
  fontFamily: settings?.font_family || undefined,
  loadingAnimation:
    settings?.loading_animation === "pulse" || settings?.loading_animation === "float"
      ? settings.loading_animation
      : undefined,
  welcomeMessage: settings?.welcome_message || undefined,
  kitchenMode:
    settings?.kitchen_mode === "screen" || settings?.kitchen_mode === "printer"
      ? settings.kitchen_mode
      : undefined,
});

const fetchBranchSettingsMap = async (branchIds = []) => {
  const safeBranchIds = Array.from(new Set((branchIds || []).filter(Boolean)));

  if (safeBranchIds.length === 0) {
    return new Map();
  }

  const settingsSelects = [
    BRANCH_REMOTE_SETTINGS_SELECT,
    BRANCH_REMOTE_SETTINGS_OPERATIONAL_SELECT,
    BRANCH_REMOTE_SETTINGS_KIOSK_SELECT,
    BRANCH_REMOTE_SETTINGS_LEGACY_SELECT,
  ];
  const selectCandidates = [
    ...settingsSelects.map((fields) => `${fields}, qr_menu_style, qr_category_order, qr_subcategory_order`),
    ...settingsSelects.map((fields) => `${fields}, qr_menu_style, qr_category_order`),
    ...settingsSelects.map((fields) => `${fields}, qr_menu_style, qr_subcategory_order`),
    ...settingsSelects.map((fields) => `${fields}, qr_menu_style`),
    ...settingsSelects,
  ];

  let data = null;
  let error = null;

  for (const selectClause of selectCandidates) {
    const result = await supabase
      .from("branch_settings")
      .select(selectClause)
      .in("branch_id", safeBranchIds);

    data = result.data;
    error = result.error;

    if (!error) {
      break;
    }

    if (!isMissingBranchSettingsColumnsError(error)) {
      break;
    }
  }

  if (error) {
    throw normalizeError(error, "No se pudo cargar la configuracion operativa de las sucursales.");
  }

  return new Map(
    (data || []).map((settings) => [
      settings.branch_id,
      normalizeBranchSettings(settings),
    ])
  );
};

const normalizeBranch = (branch, options = {}) => {
  const settings = options.settings || {};
  const availableModes = (options.availableModes || [normalizeBranchMode(options.role)]).filter(
    (mode) => mode && (mode !== "mozo" || settings.waiterEnabled !== false)
  );
  const defaultMode = getDefaultBranchMode(availableModes);

  return {
    id: branch.id,
    companyId: branch.company_id || options.companyId || null,
    name: branch.name || "",
    slug: branch.slug || "",
    phone: branch.phone || "",
    address: branch.address || "",
    description: branch.description || "",
    timezone: branch.timezone || "America/Argentina/Buenos_Aires",
    status: branch.status || "active",
    role: options.role || "admin_limitado",
    availableModes,
    defaultMode,
    permissions: options.permissions || FULL_BRANCH_PERMISSIONS,
    createdAt: branch.created_at || null,
    ...settings,
  };
};

const getOwnedCompany = async (userId) => {
  const { data, error } = await supabase
    .from("companies")
    .select("*")
    .eq("owner_user_id", userId)
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw normalizeError(error, "No se pudo cargar la empresa principal.");
  }

  if (!data) {
    return null;
  }

  return {
    company: normalizeCompany(data, "admin_general", "admin_general"),
    companyRole: "admin_general",
    workspaceRole: "admin_general",
  };
};

const getCompanyMembership = async (userId) => {
  const owned = await getOwnedCompany(userId);
  if (owned) {
    return owned;
  }

  let membershipQuery = supabase
    .from("company_users")
    .select("role, workspace_role, company:companies(*)")
    .eq("user_id", userId)
    .eq("active", true)
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();

  let { data, error } = await membershipQuery;

  if (error && isMissingWorkspaceRoleError(error)) {
    ({ data, error } = await supabase
      .from("company_users")
      .select("role, company:companies(*)")
      .eq("user_id", userId)
      .eq("active", true)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle());
  }

  if (error) {
    throw normalizeError(error, "No se pudo cargar la empresa del usuario.");
  }

  if (!data?.company) {
    return null;
  }

  return {
    company: normalizeCompany(
      data.company,
      data.role || "admin_limitado",
      data.workspace_role || data.role || "admin_limitado"
    ),
    companyRole: data.role || "admin_limitado",
    workspaceRole: data.workspace_role || data.role || "admin_limitado",
  };
};

const getBranchesForAdminGeneral = async (companyId) => {
  const { data, error } = await supabase
    .from("branches")
    .select("*")
    .eq("company_id", companyId)
    .order("created_at", { ascending: true });

  if (error) {
    throw normalizeError(error, "No se pudieron cargar las sucursales.");
  }

  const settingsMap = await fetchBranchSettingsMap((data || []).map((branch) => branch.id));

  return (data || []).map((branch) =>
    normalizeBranch(branch, {
      role: "admin_general",
      availableModes: ["admin", "cocina"],
      permissions: FULL_BRANCH_PERMISSIONS,
      settings: settingsMap.get(branch.id),
    })
  );
};

const fetchAcceptedInvitationModesMap = async (userId, companyId) => {
  const { data, error } = await supabase
    .from("user_invitations")
    .select("branch_id, workspace_role")
    .eq("company_id", companyId)
    .eq("accepted_by", userId)
    .eq("status", "accepted");

  if (error && isMissingWorkspaceRoleError(error)) {
    return new Map();
  }

  if (error) {
    throw normalizeError(
      error,
      "No se pudieron cargar los modos de acceso aceptados para las sucursales."
    );
  }

  return (data || []).reduce((map, row) => {
    if (!row?.branch_id) {
      return map;
    }

    const currentModes = map.get(row.branch_id) || [];
    const nextMode = normalizeBranchMode(row.workspace_role);

    if (nextMode && !currentModes.includes(nextMode)) {
      map.set(row.branch_id, [...currentModes, nextMode]);
    }

    return map;
  }, new Map());
};

const getBranchesForLimitedAdmin = async (
  userId,
  companyId,
  companyWorkspaceRole = "admin_limitado"
) => {
  const { data, error } = await supabase
    .from("branch_user_access")
    .select("*, branch:branches(*)")
    .eq("user_id", userId)
    .eq("active", true)
    .order("created_at", { ascending: true });

  if (error) {
    throw normalizeError(error, "No se pudieron cargar las sucursales asignadas.");
  }

  const allowedRows = (data || []).filter((row) => row?.branch?.company_id === companyId);
  const invitationModesMap = await fetchAcceptedInvitationModesMap(userId, companyId);
  const settingsMap = await fetchBranchSettingsMap(
    allowedRows.map((row) => row?.branch?.id).filter(Boolean)
  );

  return allowedRows
    .map((row) =>
      normalizeBranch(row.branch, {
        role: "admin_limitado",
        availableModes: resolveAvailableBranchModes({
          companyRole: "admin_limitado",
          companyWorkspaceRole,
          invitationModes: invitationModesMap.get(row.branch.id) || [],
        }),
        permissions: normalizeBranchPermissions(row),
        settings: settingsMap.get(row.branch.id),
      })
    )
    .filter((branch) => Array.isArray(branch?.availableModes) && branch.availableModes.length > 0);
};

export const getWorkspaceForUser = async (userId) => {
  ensureSupabaseReady();

  if (!userId) {
    return null;
  }

  const membership = await getCompanyMembership(userId);

  if (!membership?.company) {
    return {
      company: null,
      branches: [],
      companyRole: null,
    };
  }

  if (membership.company.status === "cancelled") {
    return { company: membership.company, branches: [], companyRole: membership.companyRole };
  }

  const branches =
    membership.companyRole === "admin_general" ||
    membership.workspaceRole === "admin_general"
      ? await getBranchesForAdminGeneral(membership.company.id)
      : await getBranchesForLimitedAdmin(
          userId,
          membership.company.id,
          membership.workspaceRole
        );

  return {
    company: membership.company,
    branches,
    companyRole: membership.companyRole,
  };
};

const insertCompany = async (payload) => {
  const baseCompany = {
    owner_user_id: payload.userId,
    name: payload.companyName.trim(),
    legal_name: payload.legalName?.trim() || null,
    phone: payload.companyPhone?.trim() || payload.branchPhone?.trim() || null,
  };

  const attemptInsert = async (slug) =>
    supabase
      .from("companies")
      .insert({
        ...baseCompany,
        slug,
      })
      .select("*")
      .single();

  let slug = createSlug(baseCompany.name, "empresa");
  let { data, error } = await attemptInsert(slug);

  if (error?.code === "23505") {
    slug = createSlug(baseCompany.name, "empresa", Date.now().toString().slice(-6));
    ({ data, error } = await attemptInsert(slug));
  }

  if (error) {
    throw normalizeError(error, "No se pudo crear la empresa.");
  }

  return data;
};

const insertInitialBranch = async (companyId, payload) => {
  const branchName = payload.branchName?.trim() || "Casa Central";
  const baseBranch = {
    company_id: companyId,
    name: branchName,
    phone: payload.branchPhone?.trim() || null,
    address: payload.branchAddress?.trim() || null,
    description: payload.branchDescription?.trim() || null,
  };

  const attemptInsert = async (slug) =>
    supabase
      .from("branches")
      .insert({
        ...baseBranch,
        slug,
      })
      .select("*")
      .single();

  let slug = createSlug(branchName, "sucursal");
  let { data, error } = await attemptInsert(slug);

  if (error?.code === "23505") {
    slug = createSlug(branchName, "sucursal", Date.now().toString().slice(-6));
    ({ data, error } = await attemptInsert(slug));
  }

  if (error) {
    throw normalizeError(error, "No se pudo crear la primera sucursal.");
  }

  return data;
};

const syncInitialBranchOperationalSettings = async (branchId, payload) => {
  await ensureBranchSettingsSupportsKioskMode();

  const nextServiceMode = payload?.serviceMode === "kiosk" ? "kiosk" : "salon";
  const nextKioskMenuMode =
    payload?.kioskMenuMode === "custom" ? "custom" : "current";
  const nextKioskStationCount = Math.min(
    12,
    Math.max(1, Number(payload?.kioskStationCount || 1) || 1)
  );
  const nextSalonEnabled =
    nextServiceMode === "kiosk" ? false : payload?.salonEnabled !== false;
  const nextDeliveryEnabled = payload?.deliveryEnabled === true;
  const nextWaiterEnabled =
    nextSalonEnabled && payload?.waiterEnabled !== false;
  const nextStockEnabled = payload?.stockEnabled !== false;
  const nextAiEnabled = payload?.aiEnabled !== false;
  const nextWePedidosEnabled =
    nextDeliveryEnabled && payload?.wePedidosEnabled === true;

  const { error: branchSettingsError } = await supabase.from("branch_settings").upsert({
    branch_id: branchId,
    service_mode: nextServiceMode,
    salon_enabled: nextSalonEnabled,
    delivery_enabled: nextDeliveryEnabled,
    operational_setup_completed: true,
    stock_enabled: nextStockEnabled,
    waiter_enabled: nextWaiterEnabled,
    ai_enabled: nextAiEnabled,
    kiosk_menu_mode: nextKioskMenuMode,
    kiosk_station_count: nextKioskStationCount,
  });

  if (branchSettingsError) {
    throw normalizeError(
      branchSettingsError,
      "La sucursal se creo, pero no se pudo guardar su modalidad operativa."
    );
  }

  const { error: weSettingsError } = await supabase.from("we_branch_settings").upsert({
    branch_id: branchId,
    enabled: nextWePedidosEnabled,
    accepting_orders: true,
    price_level: 2,
    updated_at: new Date().toISOString(),
  });

  if (weSettingsError) {
    throw normalizeError(
      weSettingsError,
      "La sucursal se creo, pero no se pudo guardar la integracion con We! pedidos."
    );
  }

  return {
    settings: {
      serviceMode: nextServiceMode,
      salonEnabled: nextSalonEnabled,
      deliveryEnabled: nextDeliveryEnabled,
      operationalSetupCompleted: true,
      stockEnabled: nextStockEnabled,
      waiterEnabled: nextWaiterEnabled,
      aiEnabled: nextAiEnabled,
      wePedidosEnabled: nextWePedidosEnabled,
      kioskMenuMode: nextKioskMenuMode,
      kioskStationCount: nextKioskStationCount,
    },
  };
};

export const createCompanyWithInitialBranch = async (userId, payload) => {
  ensureSupabaseReady();

  if (!userId) {
    throw new Error("No hay un usuario autenticado para crear la empresa.");
  }

  if (!payload.companyName?.trim()) {
    throw new Error("El nombre de la empresa es obligatorio.");
  }

  if (!payload.branchName?.trim()) {
    throw new Error("El nombre de la primera sucursal es obligatorio.");
  }

  if (payload.contractAcceptance) {
    const registered = await registerRestaurantWithContract(payload);
    return {
      company: normalizeCompany(registered.company, "admin_general"),
      branch: normalizeBranch(registered.branch, {
        role: "admin_general", permissions: FULL_BRANCH_PERMISSIONS, settings: registered.settings,
      }),
      acceptanceId: registered.acceptanceId,
    };
  }

  const company = await insertCompany({
    ...payload,
    userId,
  });
  await upsertCompanySettings(company.id, payload);

  const branch = await insertInitialBranch(company.id, payload);
  const branchOperationalSettings = await syncInitialBranchOperationalSettings(
    branch.id,
    payload
  );

  return {
    company: normalizeCompany(company, "admin_general"),
    branch: normalizeBranch(branch, {
      role: "admin_general",
      permissions: FULL_BRANCH_PERMISSIONS,
      settings: branchOperationalSettings.settings,
    }),
  };
};

export const createInitialBranchForCompany = async (company, payload) => {
  ensureSupabaseReady();

  if (!company?.id) {
    throw new Error("No encontramos una empresa activa para crear la sucursal.");
  }

  if (!payload.branchName?.trim()) {
    throw new Error("El nombre de la primera sucursal es obligatorio.");
  }

  const branch = await insertInitialBranch(company.id, payload);
  const branchOperationalSettings = await syncInitialBranchOperationalSettings(
    branch.id,
    payload
  );

  return {
    company,
    branch: normalizeBranch(branch, {
      role: company.role || "admin_general",
      permissions: FULL_BRANCH_PERMISSIONS,
      settings: branchOperationalSettings.settings,
    }),
  };
};
